import { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Flex, FullscreenTakeover, toast, QueryLoader } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { globalEnv } from "~/global-env";
import {
  useGetPaymentAccountQuery,
  useUpdatePaymentAccountMutation,
} from "~/graphql/supergraphHooks";
import { getProfileName } from "../../Profiles/utils";
import { getPaymentInstructionSchema } from "../AccountForm/schema";
import { prepareUpdateRequestPayload } from "../BeneficiaryAccountModal/utils";
import { usePaymentInstructions } from "../PaymentInstructionsContext";
import { PaymentInstructionFormValues } from "../types";
import { getAccountInitialValues } from "../utils";
import BeneficiaryAccountForm from "./BeneficiaryAccountForm";
import BeneficiaryProfileSearch from "./BeneficiaryProfileSearch";
import { bottomButtonBar } from "./styles_vanilla.css";

type BeneficiaryFullScreenProps = {
  title: string;
  accountHolderName: string;
};

const BeneficiaryFullScreen = ({ title, accountHolderName }: BeneficiaryFullScreenProps) => {
  const history = useHistory();
  const caseId = history.location.pathname.split("/case/")[1]?.split("/")[0];
  const { paymentAccountId } = useParams<{ paymentAccountId: string }>();
  const { programId, paymentDataProfiles, paymentPrefId } = usePaymentInstructions();
  const paymentBeneficiaryName = paymentDataProfiles?.[0]
    ? getProfileName(paymentDataProfiles[0])
    : undefined;

  const queryResult = useGetPaymentAccountQuery({
    variables: { id: parseInt(paymentAccountId), programId: programId },
    fetchPolicy: "network-only",
    skip: !paymentAccountId,
  });

  const [updatePaymentAccountMutation] = useUpdatePaymentAccountMutation({
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
    refetchQueries: [
      "GetInvestorBankDetails",
      "GetPaymentAccount",
      "GetCaseInvestingProfileRiskRating",
    ],
    awaitRefetchQueries: true,
  });

  const onExit = () => {
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/payment-instructions`);
  };

  const initialValues = useMemo(() => {
    return getAccountInitialValues(queryResult?.data?.compliancePaymentAccount, true);
  }, [queryResult?.data?.compliancePaymentAccount]);

  const handleFormSubmit = async (values: PaymentInstructionFormValues) => {
    const requestBody = prepareUpdateRequestPayload(
      values,
      initialValues,
      queryResult?.data?.compliancePaymentAccount || undefined,
    );

    await updatePaymentAccountMutation({
      variables: {
        caseId: parseInt(caseId),
        paymentAccountId: parseInt(paymentAccountId),
        data: requestBody,
      },
    });
    toast.success("Bank account updated");
    onExit();
  };

  return (
    <QueryLoader result={queryResult}>
      <FullscreenTakeover>
        <FullscreenTakeover.Header title={title} onExit={onExit} />
        <FullscreenTakeover.Body>
          <Form<PaymentInstructionFormValues>
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            yupValidationSchema={getPaymentInstructionSchema(!paymentPrefId)}
            submitOnlyChangedValues={false}
          >
            <Flex flexDirection="column" gap={24}>
              <BeneficiaryProfileSearch
                accountHolderName={
                  paymentBeneficiaryName ? paymentBeneficiaryName : accountHolderName
                }
              />
              <BeneficiaryAccountForm />
              <Flex className={bottomButtonBar} gap={10}>
                <CancelButton data-testid="beneficiary-cancel-button" onClick={onExit} />
                <SubmitButton data-testid="beneficiary-save-button">Save</SubmitButton>
              </Flex>
            </Flex>
          </Form>
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    </QueryLoader>
  );
};
export default BeneficiaryFullScreen;
